// Configuration utility for environment variables
import { config as envConfigs } from './env';
import { AppConfig, LogLevel } from '../types';

// Get environment variable with fallback
const getEnvVar = (key: string, defaultValue: string | null = null): string => {
  // First try to get from process.env (for actual env vars - this is what CRA embeds at build time)
  // Check both the exact key and if it exists as a string
  const envValue = process.env[key];
  if (envValue !== undefined && envValue !== null && envValue !== '') {
    return envValue as string;
  }
  
  // Then try to get from our config file based on current environment
  const currentEnv = (process.env.NODE_ENV || 'production') as keyof typeof envConfigs;
  if (currentEnv in envConfigs) {
    const envConfigForEnv = envConfigs[currentEnv];
    const envConfigValue = envConfigForEnv[key as keyof typeof envConfigForEnv];
    if (envConfigValue !== undefined && envConfigValue !== null) {
      return String(envConfigValue);
    }
  }
  
  // Return default value
  return defaultValue || '';
};

// Configuration object
const config: AppConfig = {
  // App info
  appName: getEnvVar('REACT_APP_NAME', 'CPFGS Web'),
  appVersion: getEnvVar('REACT_APP_VERSION', '1.0.0'),
  appDescription: getEnvVar('REACT_APP_DESCRIPTION', 'CPFGS Web Application'),
  
  // Environment
  nodeEnv: getEnvVar('NODE_ENV', 'development'),
  reactAppEnv: getEnvVar('REACT_APP_ENV', 'development'),
  
  // API
  apiUrl: getEnvVar('REACT_APP_API_URL', 'http://localhost:3001/api'),
  apiTimeout: parseInt(getEnvVar('REACT_APP_API_TIMEOUT', '10000')),
  
  // Feature flags
  enableDebug: getEnvVar('REACT_APP_ENABLE_DEBUG', 'true') === 'true',
  enableAnalytics: getEnvVar('REACT_APP_ENABLE_ANALYTICS', 'false') === 'true',
  enableHotReload: getEnvVar('REACT_APP_ENABLE_HOT_RELOAD', 'true') === 'true',
  
  // UI
  theme: getEnvVar('REACT_APP_THEME', 'default'),
  language: getEnvVar('REACT_APP_LANGUAGE', 'th'),
  
  // Development
  showDevTools: getEnvVar('REACT_APP_SHOW_DEV_TOOLS', 'false') === 'true',
  logLevel: getEnvVar('REACT_APP_LOG_LEVEL', 'info') as LogLevel,
  
  // Utility functions
  isDevelopment: (): boolean => config.nodeEnv === 'development',
  isProduction: (): boolean => config.nodeEnv === 'production',
  isTest: (): boolean => config.nodeEnv === 'test',
  
  // Debug logging
  log: (message: string, level: LogLevel = 'info'): void => {
    if (config.enableDebug) {
      const levels: Record<LogLevel, number> = { error: 0, warn: 1, info: 2, debug: 3 };
      const currentLevel = levels[config.logLevel] || 2;
      const messageLevel = levels[level] || 2;
      
      if (messageLevel <= currentLevel) {
        console.log(`[${config.appName}] ${message}`);
      }
    }
  }
};

export default config;
